import { useState } from 'react';
import { ChevronDown, ChevronRight, Loader2, CheckCircle2, XCircle, Wrench } from 'lucide-react';
import type { ToolActivity } from '@/types/chat';

// ToolCallCard renders one tool invocation as an expandable card,
// modeled on PRD §13.2 Screen 3 (Step indicator + tool call visualization).
export function ToolCallCard({ tool }: { tool: ToolActivity }) {
  const [open, setOpen] = useState(false);

  const Status =
    tool.status === 'running' ? (
      <Loader2 className="h-3.5 w-3.5 animate-spin text-primary" />
    ) : tool.status === 'error' ? (
      <XCircle className="h-3.5 w-3.5 text-red-500" />
    ) : (
      <CheckCircle2 className="h-3.5 w-3.5 text-green-500" />
    );

  const label =
    tool.status === 'running'
      ? `Using ${tool.name}…`
      : tool.summary || `${tool.name} (${tool.status})`;

  const inputPreview =
    tool.input && typeof tool.input === 'object'
      ? JSON.stringify(tool.input)
      : tool.input != null
      ? String(tool.input)
      : '';

  return (
    <div
      className={[
        'rounded-md border text-xs',
        tool.status === 'error'
          ? 'border-red-200 bg-red-50 dark:border-red-900 dark:bg-red-900/20'
          : 'border-border bg-surface dark:border-border-dark dark:bg-surface-dark',
      ].join(' ')}
    >
      <button
        type="button"
        className="flex w-full items-center gap-2 px-2 py-1.5 text-left"
        onClick={() => setOpen((v) => !v)}
      >
        {open ? (
          <ChevronDown className="h-3 w-3 text-text-secondary" />
        ) : (
          <ChevronRight className="h-3 w-3 text-text-secondary" />
        )}
        <Wrench className="h-3.5 w-3.5 text-text-secondary" />
        <span className="flex-1 truncate">{label}</span>
        {tool.duration_ms != null && tool.status !== 'running' && (
          <span className="text-[10px] text-text-secondary">{tool.duration_ms}ms</span>
        )}
        {Status}
      </button>
      {open && (
        <div className="border-t border-border px-2 py-1.5 dark:border-border-dark space-y-1">
          <div className="text-[10px] uppercase tracking-wide text-text-secondary">Tool</div>
          <div className="font-mono">{tool.name}</div>

          {inputPreview && (
            <>
              <div className="mt-1 text-[10px] uppercase tracking-wide text-text-secondary">Input</div>
              <pre className="max-h-24 overflow-auto rounded bg-black/5 p-1.5 font-mono text-[11px] dark:bg-white/5">
                {inputPreview}
              </pre>
            </>
          )}

          {tool.output && (
            <>
              <div className="mt-1 text-[10px] uppercase tracking-wide text-text-secondary">Output</div>
              <pre className="max-h-40 overflow-auto whitespace-pre-wrap rounded bg-black/5 p-1.5 font-mono text-[11px] dark:bg-white/5">
                {tool.output}
              </pre>
            </>
          )}
        </div>
      )}
    </div>
  );
}
